import { DOCUMENT } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';

import { BridgeService } from '@ng2/services';
import { DialogFocusIsolationService } from '@shared/services/dialog-focus-isolation.service';
import { DialogFocusRestoreService } from '@shared/services/dialog-focus-restore.service';
import { routeDataSignal } from '@shared/utils';
import { AiStore, TenantStore, UserStore } from '@signal-store';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  styleUrl: './app.component.scss',
})
export class AppComponent {
  readonly bridgeService = inject(BridgeService);
  readonly userStore = inject(UserStore);
  readonly tenantStore = inject(TenantStore);
  readonly aiStore = inject(AiStore);

  private readonly router = inject(Router);
  private readonly renderer = inject(Renderer2);
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogFocusIsolation = inject(DialogFocusIsolationService);
  private readonly dialogFocusRestore = inject(DialogFocusRestoreService);

  private isResizing = false;

  /** Hides the header when the active route declares `data: { hideHeader: true }`. */
  private readonly hideHeaderOnRoute = routeDataSignal<boolean>(this.router, 'hideHeader');

  readonly showHeader = computed(
    () =>
      !this.userStore.isLoading() &&
      !!this.tenantStore.selectedTenant() &&
      !this.userStore.hideHeader() &&
      !this.hideHeaderOnRoute(),
  );

  constructor() {
    this.dialogFocusIsolation.init();
    this.dialogFocusRestore.init();
    this.syncSidebarState();
    this.initResizeListeners();
  }

  startResize(event: MouseEvent): void {
    this.isResizing = true;
    event.preventDefault();
  }

  private syncSidebarState(): void {
    effect(() => {
      const isOpen = this.aiStore.isOpen();
      const width = this.aiStore.sidebarWidth();

      if (isOpen) {
        this.renderer.addClass(this.doc.body, 'ai-sidebar-open');
        this.doc.documentElement.style.setProperty('--ai-sidebar-width', `${width}px`);
      } else {
        this.renderer.removeClass(this.doc.body, 'ai-sidebar-open');
        this.doc.documentElement.style.setProperty('--ai-sidebar-width', '0px');
      }
    });
  }

  private initResizeListeners(): void {
    fromEvent<MouseEvent>(this.doc, 'mousemove')
      .pipe(
        filter(() => this.isResizing),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ clientX }) => {
        const newWidth = (this.doc.defaultView?.innerWidth ?? 0) - clientX;
        if (newWidth > 300 && newWidth < 800) {
          this.aiStore.updateSidebarWidth(newWidth);
        }
      });

    fromEvent(this.doc, 'mouseup')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isResizing = false;
      });
  }
}
